'use client';

import { Text } from '@lichtblick/photon/atoms/text/text';
import classNames from 'classnames';
import { type FC, useEffect, useRef } from 'react';
import { RichText } from '../../helpers/RichText';
import type { SliderSectionType, TimelineSlideType } from '../../types/storyblok';
import { AImage } from '../AImage';
import styles from './timeline-slide.module.scss';

const scrollTargetIntoView = (e: { currentTarget: HTMLLIElement }) => {
  e.currentTarget.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
    inline: 'center',
  });
};

export const TimelineSlide: FC<
  TimelineSlideType & {
    background: SliderSectionType['background'];
    id: string;
  }
> = ({ id, image, text, timestamp, background }) => {
  const ref = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const slide = ref.current;

    const observer = new IntersectionObserver(
      (entries) => {
        const intersectionRatio = entries.find((entry) => entry.target === ref.current)?.intersectionRatio ?? 1;

        slide.style.setProperty('--intersection-ratio', String(intersectionRatio > 0.9 ? 1 : intersectionRatio));
      },
      {
        threshold: Array.from({ length: 11 }, (_, i) => i / 10),
        root: slide.parentElement,
      },
    );

    observer.observe(slide);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: TODO
    // biome-ignore lint/a11y/useKeyWithClickEvents: TODO
    <li
      className={classNames(styles.slide, styles[`bg-${background}`])}
      id={id}
      onClick={scrollTargetIntoView}
      onFocus={scrollTargetIntoView}
      ref={ref}
    >
      <Text className={styles.timestamp} bold size="s">
        {timestamp}
      </Text>

      {image && <AImage imageMobile={image} altText={image.alt ?? ''} className={styles.image} />}

      <RichText richText={text} />
    </li>
  );
};
